import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { TopUp, TopUpAllocation } from 'models/TopUp';
import { getTopUpsAPI } from 'services/topUpsApi';
import { DashboardSkeleton } from 'components/Skeleton/DashboardSkeleton';
import { CustomCard } from 'components/CustomCard';
import { toastWarning } from 'utils/toastMethod';
import CustomTable from 'components/Table/CustomTable';
import PageLayout from 'components/Layout/PageLayout';
import PageLayoutSkeleton from 'components/Layout/PageLayout/skeleton';

const currencyFormatter = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

const formatCurrency = (cents: number) => currencyFormatter.format(cents / 100);

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('it-IT', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div style={{ display: 'flex', marginBottom: '8px' }}>
    <div style={{ minWidth: '180px', fontWeight: 600, color: '#202020' }}>{label}</div>
    <div style={{ minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{value}</div>
  </div>
);

export default function TopUpDetail() {
  const { topUpId } = useParams<{ topUpId: string }>();
  const navigate = useNavigate();
  const [topUp, setTopUp] = useState<TopUp | undefined>();
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!topUpId) {
      setNotFound(true);
      return;
    }
    getTopUpsAPI()
      .then((topUps: TopUp[]) => {
        const found = topUps?.find((item) => item.id === topUpId);
        if (found) setTopUp(found);
        else setNotFound(true);
      })
      .catch(() => {
        setNotFound(true);
        toastWarning('Qualcosa è andato storto!');
      });
  }, [topUpId]);

  const columnHelper = createColumnHelper<TopUpAllocation>();
  const allocationColumns = [
    columnHelper.accessor('paymentId', {
      header: () => 'ID PAGAMENTO',
      cell: (paymentId) => paymentId.getValue(),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor('amount', {
      header: () => 'IMPORTO',
      cell: (amount) => formatCurrency(amount.getValue() as number),
      enableColumnFilter: false,
      enableSorting: false,
    }),
  ];

  if (notFound) {
    return (
      <PageLayout titlePage="Dettaglio ricarica">
        <h4>Ricarica non trovata</h4>
        <button className="btn" style={{ cursor: 'pointer' }} onClick={() => navigate('/topups')}>
          Torna alla lista ricariche
        </button>
      </PageLayout>
    );
  }

  if (!topUp) {
    return (
      <PageLayoutSkeleton>
        <DashboardSkeleton />
      </PageLayoutSkeleton>
    );
  }

  return (
    <PageLayout
      titlePage={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={() => navigate('/topups')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0 4px',
              fontSize: '28px',
              fontWeight: 600,
              lineHeight: 1,
            }}
            aria-label="Torna alla lista ricariche"
          >
            &#8592;
          </button>
          <span>Dettaglio ricarica</span>
        </div>
      }
      subtitlePage={`Ricarica ${topUp.id}`}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '20px' }}>
        <CustomCard title={<h3>Informazioni ricarica</h3>} width="380px">
          <DetailRow label="Stato" value={topUp.status} />
          <DetailRow label="Importo" value={formatCurrency(topUp.amount)} />
          <DetailRow label="Data" value={formatDate(topUp.createdAt)} />
          <DetailRow label="Creata da" value={topUp.createdBy} />
          <DetailRow label="Note" value={topUp.notes} />
        </CustomCard>
      </div>

      <h3>Allocazioni</h3>
      {topUp.allocations && topUp.allocations.length > 0 ? (
        <CustomTable
          data={topUp.allocations}
          columns={allocationColumns}
          pageStartIndex={0}
          pageMaxSize={10}
          columnsVisibility={{}}
        />
      ) : (
        <h4>Nessuna allocazione presente</h4>
      )}
    </PageLayout>
  );
}
